import { motion, useMotionValueEvent, useSpring, useTransform, type HTMLMotionProps } from 'motion/react';
import { useEffect } from 'react';
import { formatNumber } from '../lib/utils';
import type { Locale } from '@worldcoo/monolith/core';

export function AnimateNumber({
	locale,
	value,
	suffix,
	testId,
	onAnimateComplete,
	decimalDigits = 0,
	...props
}: {
	locale: Locale;
	value: number;
	suffix?: string;
	testId?: string;
	onAnimateComplete?: () => void;
	decimalDigits?: number;
} & HTMLMotionProps<'div'>) {
	const MotionComponent = motion.create('div');
	const motionValue = useSpring(value, { bounce: 0, restDelta: 1 });
	const motionFormattedValue = useTransform(
		motionValue,
		(current) => `${formatNumber(locale, current, decimalDigits)}${suffix ? ` ${suffix}` : ''}`,
	);

	useMotionValueEvent(motionValue, 'change', (latestValue) => {
		if (value === latestValue) onAnimateComplete?.();
	});

	useEffect(() => {
		motionValue.set(value);
	}, [motionValue, value]);

	return (
		<MotionComponent
			className="inline-block tracking-tight tabular-nums"
			data-testid={testId}
			data-value={value}
			{...props}
		>
			{motionFormattedValue}
		</MotionComponent>
	);
}
